import { useCallback, useEffect, useRef } from "react";
import { create } from "zustand";

/** No input for this long ⇒ ambient polling pauses until the next input. */
const IDLE_AFTER_MS = 60 * 60 * 1000;
const IDLE_CHECK_MS = 60 * 1000;

const ACTIVITY_EVENTS = [
  "pointerdown",
  "pointermove",
  "keydown",
  "wheel",
  "touchstart",
] as const;

let lastActivityMs = Date.now();
let installed = false;

interface ActivityState {
  isIdle: boolean;
}

export const useActivityStore = create<ActivityState>(() => ({
  isIdle: false,
}));

const markActive = () => {
  lastActivityMs = Date.now();
  if (useActivityStore.getState().isIdle) {
    useActivityStore.setState({ isIdle: false });
  }
};

export function installActivityTracker(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;
  ACTIVITY_EVENTS.forEach((event) =>
    window.addEventListener(event, markActive, { passive: true }),
  );
  // Cmd/Alt-Tab back to the window fires no input event — only focus.
  window.addEventListener("focus", markActive);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") markActive();
  });
  window.setInterval(() => {
    if (
      !useActivityStore.getState().isIdle &&
      Date.now() - lastActivityMs >= IDLE_AFTER_MS
    ) {
      useActivityStore.setState({ isIdle: true });
    }
  }, IDLE_CHECK_MS);
}

/** Gate for ambient poll ticks: tab visible and user not idle. */
export const pollingAllowed = (): boolean =>
  document.visibilityState === "visible" && !useActivityStore.getState().isIdle;

/** Idle-only gate — keeps polling in hidden tabs (e.g. the notification
 *  badge) until the user goes idle. */
export const userActive = (): boolean => !useActivityStore.getState().isIdle;

/** Runs cb once when polling resumes: idle→active or tab back to visible.
 *  Window focus is deliberately NOT a trigger — an unfocused-but-visible
 *  window keeps polling (pollingAllowed checks visibility, not focus), so
 *  its data is never stale on return; a fully covered window goes hidden
 *  and the visibility transition already refetches. */
export function useResumeRefresh(cb: () => void): void {
  const isIdle = useActivityStore((s) => s.isIdle);
  const prevIdle = useRef(isIdle);
  const lastRunMs = useRef(0);
  const run = useCallback(() => {
    const now = Date.now();
    if (now - lastRunMs.current < 1000) return;
    lastRunMs.current = now;
    cb();
  }, [cb]);
  useEffect(() => {
    if (prevIdle.current && !isIdle) run();
    prevIdle.current = isIdle;
  }, [isIdle, run]);
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") run();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [run]);
}
